import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Key, Shield, Zap, ArrowRight } from 'lucide-react';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
interface SystemStatus {
  apiKeysActive: number;
  isAdmin: boolean;
  systemReady: boolean;
}
export const CustomerReadinessNotice = () => {
  const {
    user
  } = useSecureAuthContext();
  const navigate = useNavigate();
  const [status, setStatus] = useState<SystemStatus>({
    apiKeysActive: 0,
    isAdmin: false,
    systemReady: false
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (user?.email) {
      checkSystemStatus();
    }
  }, [user]);
  const checkSystemStatus = async () => {
    try {
      // Check if user is admin
      const {
        data: adminData
      } = await supabase.rpc('is_admin', {
        user_email: user?.email
      });

      // Check API keys status (admin only)
      let apiKeyCount = 0;
      if (adminData) {
        try {
          const {
            data: keyData
          } = await supabase.rpc('get_available_api_key');
          apiKeyCount = (keyData as any)?.available ? 1 : 0;
        } catch (error) {
          // If API key check fails, assume no keys available
          apiKeyCount = 0;
        }
      }
      setStatus({
        apiKeysActive: apiKeyCount,
        isAdmin: !!adminData,
        systemReady: apiKeyCount > 0
      });
    } catch (error) {
      console.error('Error checking system status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Don't show if not admin or if system is ready
  if (loading || !status.isAdmin || status.systemReady) {
    return null;
  }
  return <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20 mb-6">
      <CardContent className="p-6 bg-cyan-950 rounded-none">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-orange-400" />
          </div>
          
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="text-white font-semibold text-lg mb-2">
                🚨 Final Setup Required for Customer Readiness
              </h3>
              <p className="text-orange-200 text-sm leading-relaxed">
                Your system is <strong>99% ready</strong> for customers! One final step is needed to enable image generation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center space-x-2 text-green-300">
                <CheckCircle className="w-4 h-4" />
                <span>Security Configured</span>
              </div>
              <div className="flex items-center space-x-2 text-green-300">
                <CheckCircle className="w-4 h-4" />
                <span>Authentication Ready</span>
              </div>
            </div>


            <div className="text-xs text-orange-200/80">
              <Shield className="w-4 h-4 inline mr-1" />
              <strong>Security Status:</strong> Maximum security achieved with 27 RLS-protected tables and 55 policies
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
};
export default CustomerReadinessNotice;